import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { GraduationCap, LogOut, BookOpen, Award, User, Play, FileText, Clock, CheckCircle2, XCircle, Loader2, Download, ArrowLeft, ExternalLink, Receipt, ShieldCheck, IdCard } from 'lucide-react';
import StudentKycForm from './StudentKycForm';

const round = (n) => Math.round(n * 100) / 100;
const statusColor = (s) => s === 'active' ? 'bg-brand-success/15 text-brand-success' : s === 'payment_pending' ? 'bg-amber-100 text-amber-700' : s === 'completed' ? 'bg-brand-primary/15 text-brand-primary' : 'bg-slate-100 text-slate-600';

export default function StudentPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [downloadingInvoice, setDownloadingInvoice] = useState('');
  const [tab, setTab] = useState('my-courses');
  const [enrollDialog, setEnrollDialog] = useState(null); // course object
  const [enrollForm, setEnrollForm] = useState({ payment_mode: 'upi', payment_reference: '', notes: '' });
  const [enrolling, setEnrolling] = useState(false);

  const [courseView, setCourseView] = useState(null); // full course detail
  const [examView, setExamView] = useState(null); // {course, answers, result}
  const [lessonProgress, setLessonProgress] = useState({}); // {lessonIdx: {watched_pct, completed}}
  const progressThrottleRef = useRef({});
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

  const saveProgress = useCallback(async (courseId, lessonIdx, patch) => {
    try {
      const { data } = await api.post('/academy/progress', {
        course_id: courseId, lesson_index: lessonIdx,
        watched_pct: patch.watched_pct || 0,
        last_position_sec: patch.last_position_sec || 0,
        completed: patch.completed || false,
      });
      setLessonProgress(prev => ({
        ...prev,
        [lessonIdx]: { ...prev[lessonIdx], ...patch, completed: patch.completed || (patch.watched_pct || 0) >= 90 },
      }));
      return data;
    } catch (err) { console.error('Progress save failed:', err); }
  }, []);

  const throttledProgress = useCallback((courseId, lessonIdx, videoEl) => {
    const key = `${courseId}-${lessonIdx}`;
    const now = Date.now();
    if (progressThrottleRef.current[key] && (now - progressThrottleRef.current[key]) < 10000) return;
    progressThrottleRef.current[key] = now;
    const pct = videoEl.duration > 0 ? Math.round((videoEl.currentTime / videoEl.duration) * 100) : 0;
    saveProgress(courseId, lessonIdx, { watched_pct: pct, last_position_sec: videoEl.currentTime });
  }, [saveProgress]);

  // Load progress when course opens
  useEffect(() => {
    if (!courseView?.id) return;
    api.get(`/academy/progress/${courseView.id}`).then(r => {
      const map = {};
      (r.data || []).forEach(p => { map[p.lesson_index] = p; });
      setLessonProgress(map);
    }).catch(() => {});
  }, [courseView?.id]);

  const fetchAll = useCallback(async () => {
    try {
      const [meRes, coursesRes, enRes, certRes, atRes, invRes] = await Promise.all([
        api.get('/students/me'),
        api.get('/courses'),
        api.get('/students/my/enrollments'),
        api.get('/certificates/my'),
        api.get('/students/my/attempts'),
        api.get('/academy/invoices/mine'),
      ]);
      setMe(meRes.data);
      setCourses(coursesRes.data || []);
      setEnrollments(enRes.data || []);
      setCertificates(certRes.data || []);
      setAttempts(atRes.data || []);
      setInvoices(invRes.data || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Please sign in');
        navigate('/login');
      } else {
        toast.error(formatError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { console.error('Logout API failed:', err); }
    localStorage.removeItem('student_token'); // legacy cleanup — no longer written
    navigate('/login');
  };

  const handleEnroll = async () => {
    if (!enrollDialog) return;
    setEnrolling(true);
    try {
      await api.post('/students/enroll', { course_id: enrollDialog.id, ...enrollForm });
      toast.success('Enrollment submitted. Admin will approve once payment is verified.');
      setEnrollDialog(null);
      setEnrollForm({ payment_mode: 'upi', payment_reference: '', notes: '' });
      fetchAll();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setEnrolling(false);
    }
  };

  const openCourse = async (courseId) => {
    try {
      const { data } = await api.get(`/courses/${courseId}`);
      setCourseView(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const startExam = async () => {
    if (!courseView) return;
    setExamView({ course: courseView, answers: Array(courseView.exam_questions?.length || 0).fill(-1), result: null, submitting: false });
  };

  const submitExam = async () => {
    if (!examView) return;
    setExamView({ ...examView, submitting: true });
    try {
      const { data } = await api.post(`/exams/${examView.course.id}/attempt`, { course_id: examView.course.id, answers: examView.answers });
      setExamView({ ...examView, result: data, submitting: false });
      if (data.passed) {
        toast.success(`Congrats! You passed with ${data.score_pct}%`);
      } else {
        toast.error(`You scored ${data.score_pct}%. Passing is required.`);
      }
      fetchAll();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setExamView({ ...examView, submitting: false });
    }
  };

  const downloadCertificate = async (cert) => {
    try {
      const res = await api.get(`/certificates/${cert.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificate-${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download certificate');
    }
  };

  const downloadInvoice = async (inv) => {
    setDownloadingInvoice(inv.id);
    try {
      const res = await api.get(`/academy/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${inv.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to download invoice');
    } finally {
      setDownloadingInvoice('');
    }
  };

  const downloadIdCard = async () => {
    if (!me?.id) return;
    try {
      const res = await api.get(`/students/${me.id}/id-card`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `id-card-${me.student_id || me.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('ID card downloaded');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to download ID card');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-brand-bg">
      <Loader2 className="animate-spin text-brand-primary" size={32} />
    </div>
  );

  // ─── KYC GATE ───
  // Block portal until KYC is submitted. Show pending screen once submitted, or the form otherwise.
  if (me && me.kyc_status !== 'approved') {
    if (me.kyc_status === 'pending') {
      return (
        <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4" data-testid="kyc-pending-screen">
          <div className="max-w-lg w-full bg-white border border-brand-border rounded-2xl p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <Clock size={28} className="text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-brand-text-primary mb-2">KYC Under Review</h1>
            <p className="text-sm text-brand-text-secondary mb-1">Thanks {me.name}! We&apos;ve received your details.</p>
            <p className="text-sm text-brand-text-secondary mb-6">
              Our team is verifying your KYC — this usually takes a few hours. You&apos;ll receive an email at <span className="font-mono text-brand-text-primary">{me.email}</span> once approved.
            </p>
            <div className="grid grid-cols-2 gap-2 text-left text-xs bg-brand-bg rounded-lg p-3 mb-6">
              <div><p className="text-brand-text-secondary">Student ID</p><p className="font-mono font-bold text-brand-text-primary">{me.student_id}</p></div>
              <div><p className="text-brand-text-secondary">Submitted</p><p className="font-mono font-bold text-brand-text-primary">{(me.kyc_submitted_at || '').slice(0, 10)}</p></div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="kyc-pending-logout"><LogOut size={14} className="mr-1" /> Sign out</Button>
          </div>
        </div>
      );
    }
    // undefined | 'rejected' → show the form
    return (
      <StudentKycForm
        me={me}
        onLogout={handleLogout}
        onSubmitted={() => fetchAll()}
      />
    );
  }

  const activeEnrollments = enrollments.filter(e => e.status === 'active' || e.status === 'completed');
  const pendingEnrollments = enrollments.filter(e => e.status === 'payment_pending');
  const enrolledCourseIds = new Set(enrollments.filter(e => e.status !== 'cancelled').map(e => e.course_id));
  const availableCourses = courses.filter(c => !enrolledCourseIds.has(c.id));

  // ─── COURSE DETAIL VIEW ───
  if (courseView) {
    const enrollment = enrollments.find(e => e.course_id === courseView.id);
    const isActive = enrollment?.status === 'active' || enrollment?.status === 'completed';
    return (
      <div className="min-h-screen bg-brand-bg" data-testid="course-detail-page">
        <TopBar name={me?.name} onLogout={handleLogout} />
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
          <button onClick={() => { setCourseView(null); setExamView(null); }} className="flex items-center gap-1 text-sm text-brand-text-secondary hover:text-brand-primary mb-4" data-testid="back-to-portal">
            <ArrowLeft size={16} /> Back to portal
          </button>

          <Card className="border-brand-border bg-white overflow-hidden">
            {courseView.thumbnail_url && (
              <div className="h-40 sm:h-56 bg-cover bg-center" style={{ backgroundImage: `url(${courseView.thumbnail_url})` }} />
            )}
            <CardContent className="p-6 space-y-3">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <Badge className="bg-amber-100 text-amber-700 border-0 text-xs uppercase mb-2">{courseView.level} · {courseView.category}</Badge>
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary">{courseView.title}</h1>
                  <p className="text-sm text-brand-text-secondary mt-2 max-w-3xl">{courseView.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest text-brand-text-secondary">Fee</p>
                  <p className="font-heading text-2xl font-bold text-brand-primary">${courseView.fee_amount}</p>
                  <p className="text-xs text-brand-text-secondary">{courseView.duration_weeks} weeks</p>
                </div>
              </div>

              {!enrollment && (
                <Button onClick={() => setEnrollDialog(courseView)} className="bg-brand-primary text-white" data-testid="enroll-cta-btn">
                  Enroll Now — ${courseView.fee_amount}
                </Button>
              )}
              {enrollment?.status === 'payment_pending' && (
                <Badge className="bg-amber-100 text-amber-700 border-0 px-3 py-1.5">Awaiting admin approval — payment verification pending</Badge>
              )}
              {isActive && (
                <Badge className="bg-brand-success/15 text-brand-success border-0 px-3 py-1.5">You are enrolled — access all content below</Badge>
              )}
            </CardContent>
          </Card>

          {/* SYLLABUS */}
          <Card className="border-brand-border bg-white mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText size={16} /> Syllabus</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-2 list-decimal list-inside">
                {courseView.syllabus?.map((s, i) => (
                  <li key={i} className="text-sm text-brand-text-primary">{s}</li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {/* LESSONS - only if active */}
          {isActive && courseView.lessons?.length > 0 && (
            <Card className="border-brand-border bg-white mt-4">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Play size={16} /> Lessons</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {courseView.lessons.map((l, i) => {
                  const isUpload = l.video_type === 'upload' && l.video_storage_path;
                  const isExternal = l.video_type === 'external' && l.video_url;
                  const isYoutube = (!l.video_type || l.video_type === 'youtube') && l.video_url;
                  const prog = lessonProgress[i] || {};
                  const isDone = prog.completed || (prog.watched_pct || 0) >= 90;
                  return (
                  <div key={`lesson-${i}-${l.title}`} className="border border-brand-border/60 rounded-lg p-3 bg-brand-bg/40" data-testid={`lesson-${i}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-brand-text-primary">Lesson {i + 1}: {l.title}</p>
                        {l.is_post_recorded && <Badge className="text-[9px] border-0 bg-purple-100 text-purple-700">LIVE RECORDING{l.session_date ? ` · ${l.session_date}` : ''}</Badge>}
                        {isDone && <Badge className="text-[9px] border-0 bg-brand-success/15 text-brand-success"><CheckCircle2 size={9} className="mr-0.5" /> COMPLETED</Badge>}
                      </div>
                      {l.duration_min && <span className="text-xs text-brand-text-secondary flex items-center gap-1"><Clock size={11} /> {l.duration_min}m</span>}
                    </div>
                    {(prog.watched_pct || 0) > 0 && !isDone && (
                      <div className="h-1 rounded-full bg-brand-bg overflow-hidden mb-2">
                        <div className="h-full bg-brand-primary transition-all" style={{ width: `${prog.watched_pct}%` }} />
                      </div>
                    )}
                    {isUpload ? (
                      <div className="aspect-video rounded-lg overflow-hidden bg-black">
                        <video
                          src={`${apiBase}/academy/videos/stream?path=${encodeURIComponent(l.video_storage_path)}`}
                          controls
                          controlsList="nodownload"
                          crossOrigin="use-credentials"
                          className="w-full h-full"
                          data-testid={`video-player-${i}`}
                          onTimeUpdate={(e) => throttledProgress(courseView.id, i, e.target)}
                          onEnded={() => saveProgress(courseView.id, i, { watched_pct: 100, completed: true })}
                        />
                      </div>
                    ) : isYoutube ? (
                      <>
                        <div className="aspect-video rounded-lg overflow-hidden bg-black">
                          <iframe src={l.video_url} title={l.title} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope" allowFullScreen />
                        </div>
                        <div className="mt-2 flex justify-end">
                          <Button size="sm" variant="ghost" onClick={() => saveProgress(courseView.id, i, { watched_pct: 100, completed: true })} className="h-7 text-xs" data-testid={`mark-done-${i}`}>
                            <CheckCircle2 size={12} className="mr-1" /> Mark complete
                          </Button>
                        </div>
                      </>
                    ) : isExternal ? (
                      <div className="aspect-video rounded-lg overflow-hidden bg-black">
                        <video src={l.video_url} controls className="w-full h-full" onTimeUpdate={(e) => throttledProgress(courseView.id, i, e.target)} onEnded={() => saveProgress(courseView.id, i, { watched_pct: 100, completed: true })} data-testid={`video-player-${i}`} />
                      </div>
                    ) : null}
                    {l.pdf_url && (
                      <a href={l.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline mt-2">
                        <FileText size={12} /> Download PDF materials <ExternalLink size={11} />
                      </a>
                    )}
                    {!isUpload && !isExternal && !isYoutube && !l.pdf_url && (
                      <p className="text-xs text-brand-text-secondary italic">Content will be added by the instructor soon.</p>
                    )}
                  </div>
                );
                })}
              </CardContent>
            </Card>
          )}

          {/* EXAM - only if active */}
          {isActive && courseView.exam_questions?.length > 0 && !examView && (
            <Card className="border-brand-border bg-white mt-4">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Award size={16} /> Certification Exam</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-brand-text-secondary">
                  Test your knowledge with a {courseView.exam_questions.length}-question MCQ exam. Score {courseView.pass_percentage}% or higher to earn your certificate.
                </p>
                {enrollment?.status === 'completed' ? (
                  <Badge className="bg-brand-success/15 text-brand-success border-0">Course completed! You can retake the exam anytime.</Badge>
                ) : null}
                <Button onClick={startExam} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="start-exam-btn">
                  {enrollment?.status === 'completed' ? 'Retake Exam' : 'Start Exam'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* EXAM VIEW */}
          {examView && (
            <Card className="border-brand-border bg-white mt-4">
              <CardHeader className="pb-2"><CardTitle className="text-base">Exam — {examView.course.title}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {examView.result ? (
                  <div className="text-center py-6" data-testid="exam-result">
                    {examView.result.passed ? (
                      <>
                        <CheckCircle2 size={48} className="text-brand-success mx-auto mb-2" />
                        <h3 className="font-heading text-xl font-bold text-brand-text-primary">You passed!</h3>
                        <p className="text-3xl font-bold text-brand-success mt-2">{examView.result.score_pct}%</p>
                        <p className="text-sm text-brand-text-secondary mt-1">{examView.result.correct} of {examView.result.total} correct</p>
                        {examView.result.certificate_number && (
                          <p className="text-xs text-brand-primary mt-2">Certificate: {examView.result.certificate_number}</p>
                        )}
                        <div className="flex gap-2 justify-center mt-4">
                          <Button onClick={() => { setExamView(null); setCourseView(null); setTab('certificates'); }} className="bg-brand-primary text-white">View Certificates</Button>
                          <Button variant="outline" onClick={() => setExamView(null)}>Close</Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <XCircle size={48} className="text-brand-error mx-auto mb-2" />
                        <h3 className="font-heading text-xl font-bold text-brand-text-primary">Not passed yet</h3>
                        <p className="text-3xl font-bold text-brand-error mt-2">{examView.result.score_pct}%</p>
                        <p className="text-sm text-brand-text-secondary mt-1">{examView.result.correct} of {examView.result.total} correct — passing is {examView.course.pass_percentage}%</p>
                        <Button className="mt-4" onClick={() => setExamView({ course: examView.course, answers: Array(examView.course.exam_questions.length).fill(-1), result: null })}>Try Again</Button>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {examView.course.exam_questions.map((q, qi) => (
                      <div key={`q-${qi}-${(q.q || '').slice(0, 20)}`} className="border border-brand-border/60 rounded-lg p-3" data-testid={`exam-question-${qi}`}>
                        <p className="text-sm font-semibold text-brand-text-primary mb-2">Q{qi + 1}. {q.q}</p>
                        <div className="space-y-1.5">
                          {q.options.map((opt, oi) => (
                            <label key={`opt-${qi}-${oi}-${opt}`} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${examView.answers[qi] === oi ? 'border-brand-primary bg-brand-primary/5' : 'border-brand-border/50 hover:bg-brand-bg/60'}`}>
                              <input
                                type="radio"
                                name={`q-${qi}`}
                                checked={examView.answers[qi] === oi}
                                onChange={() => {
                                  const newAns = [...examView.answers];
                                  newAns[qi] = oi;
                                  setExamView({ ...examView, answers: newAns });
                                }}
                                data-testid={`exam-q${qi}-opt${oi}`}
                              />
                              <span className="text-sm">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Button
                      onClick={submitExam}
                      disabled={examView.answers.some(a => a === -1) || examView.submitting}
                      className="w-full bg-brand-primary text-white"
                      data-testid="submit-exam-btn"
                    >
                      {examView.submitting ? <Loader2 className="animate-spin" size={16} /> : 'Submit Exam'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <EnrollDialog course={enrollDialog} onClose={() => setEnrollDialog(null)} form={enrollForm} setForm={setEnrollForm} onSubmit={handleEnroll} enrolling={enrolling} />
      </div>
    );
  }

  // ─── MAIN PORTAL ───
  return (
    <div className="min-h-screen bg-brand-bg" data-testid="student-portal">
      <TopBar name={me?.name} onLogout={handleLogout} />

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Welcome hero */}
        <div className="bg-gradient-to-br from-[#0F1E42] to-[#2563EB] rounded-2xl p-6 sm:p-8 mb-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-2">Quantora Academy</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Welcome back, {me?.name}</h1>
            <p className="text-white/70 text-sm mt-2">Student ID: <span className="font-mono">{me?.student_id}</span></p>
            <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
              <StatCard label="Enrolled" value={activeEnrollments.length} icon={BookOpen} />
              <StatCard label="Completed" value={enrollments.filter(e => e.status === 'completed').length} icon={CheckCircle2} />
              <StatCard label="Certificates" value={certificates.length} icon={Award} />
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-brand-border rounded-lg p-1 h-auto flex-wrap gap-1 mb-6">
            {[
              { v: 'my-courses', l: 'My Courses' },
              { v: 'explore', l: 'Explore' },
              { v: 'certificates', l: 'Certificates' },
              { v: 'invoices', l: 'Invoices / Billing' },
              { v: 'profile', l: 'Profile' },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="rounded-md text-xs sm:text-sm data-[state=active]:bg-brand-primary data-[state=active]:text-white px-4 py-1.5" data-testid={`student-tab-${t.v}`}>
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* MY COURSES */}
          <TabsContent value="my-courses" className="space-y-4">
            {pendingEnrollments.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Payment pending for {pendingEnrollments.length} course(s)</p>
                  <p className="text-xs text-amber-700">Once admin verifies your payment, the course will unlock automatically.</p>
                </CardContent>
              </Card>
            )}
            {activeEnrollments.length === 0 && pendingEnrollments.length === 0 ? (
              <Card className="border-brand-border bg-white">
                <CardContent className="p-10 text-center">
                  <BookOpen size={40} className="text-brand-text-secondary mx-auto mb-3" />
                  <p className="text-brand-text-primary font-semibold">No courses yet</p>
                  <p className="text-sm text-brand-text-secondary mt-1 mb-4">Explore our catalog to start your trading education.</p>
                  <Button onClick={() => setTab('explore')} className="bg-brand-primary text-white">Explore Courses</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...activeEnrollments, ...pendingEnrollments].map(e => (
                  <Card key={e.id} className="border-brand-border bg-white hover:shadow-md transition-shadow cursor-pointer" onClick={() => e.status !== 'payment_pending' && openCourse(e.course_id)} data-testid={`enrollment-card-${e.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading text-base font-bold text-brand-text-primary">{e.course_title}</h3>
                        <Badge className={`text-[10px] border-0 ${statusColor(e.status)}`}>{e.status.replace('_', ' ')}</Badge>
                      </div>
                      <p className="text-xs text-brand-text-secondary">Enrolled {e.enrolled_at?.slice(0, 10)}</p>
                      <div className="mt-3 h-1.5 bg-brand-bg rounded-full overflow-hidden">
                        <div className="h-full bg-brand-primary transition-all" style={{ width: `${e.progress_pct || (e.status === 'completed' ? 100 : e.status === 'active' ? 10 : 0)}%` }} />
                      </div>
                      {e.status === 'active' && <Button size="sm" variant="link" className="text-brand-primary p-0 h-auto mt-2 text-xs">Continue learning →</Button>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* EXPLORE */}
          <TabsContent value="explore" className="space-y-4">
            {availableCourses.length === 0 ? (
              <Card className="border-brand-border bg-white">
                <CardContent className="p-10 text-center">
                  <p className="text-brand-text-secondary">You are enrolled in all available courses. 🎉</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableCourses.map(c => (
                  <Card key={c.id} className="border-brand-border bg-white hover:shadow-md transition-shadow overflow-hidden" data-testid={`course-card-${c.id}`}>
                    {c.thumbnail_url && (
                      <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${c.thumbnail_url})` }} />
                    )}
                    <CardContent className="p-4">
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] uppercase mb-2">{c.level}</Badge>
                      <h3 className="font-heading text-base font-bold text-brand-text-primary line-clamp-1">{c.title}</h3>
                      <p className="text-xs text-brand-text-secondary mt-1 line-clamp-2">{c.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div>
                          <p className="font-heading text-lg font-bold text-brand-primary">${c.fee_amount}</p>
                          <p className="text-[10px] text-brand-text-secondary">{c.duration_weeks} weeks</p>
                        </div>
                        <Button size="sm" onClick={() => openCourse(c.id)} className="bg-brand-primary text-white text-xs" data-testid={`view-course-${c.id}`}>
                          View
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CERTIFICATES */}
          <TabsContent value="certificates" className="space-y-4">
            {certificates.length === 0 ? (
              <Card className="border-brand-border bg-white">
                <CardContent className="p-10 text-center">
                  <Award size={40} className="text-brand-text-secondary mx-auto mb-3" />
                  <p className="text-brand-text-primary font-semibold">No certificates yet</p>
                  <p className="text-sm text-brand-text-secondary mt-1">Complete a course exam to earn your first certificate.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {certificates.map(c => (
                  <Card key={c.id} className="border-brand-border bg-white" data-testid={`certificate-card-${c.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0">
                          <Award size={22} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading text-base font-bold text-brand-text-primary">{c.course_title}</h3>
                          <p className="text-xs text-brand-text-secondary font-mono mt-0.5">{c.certificate_number}</p>
                          <p className="text-xs text-brand-text-secondary">Score: <span className="font-semibold text-brand-success">{round(c.score_pct)}%</span> · Issued {c.issued_at?.slice(0, 10)}</p>
                          <Button size="sm" onClick={() => downloadCertificate(c)} className="mt-3 bg-brand-primary text-white text-xs" data-testid={`download-cert-${c.id}`}>
                            <Download size={12} className="mr-1" /> Download PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {attempts.length > 0 && (
              <Card className="border-brand-border bg-white mt-4">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Exam Attempts History</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                      {['Course', 'Score', 'Result', 'Attempted'].map(h => <th key={h} className="text-left py-2 px-4 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {attempts.map(a => (
                        <tr key={a.id} className="border-b border-brand-border/50">
                          <td className="py-2 px-4 text-brand-text-primary">{a.course_title}</td>
                          <td className="py-2 px-4 font-mono">{a.score_pct}%</td>
                          <td className="py-2 px-4"><Badge className={`text-[10px] border-0 ${a.passed ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{a.passed ? 'PASSED' : 'FAILED'}</Badge></td>
                          <td className="py-2 px-4 text-xs text-brand-text-secondary">{a.attempted_at?.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* INVOICES / BILLING */}
          <TabsContent value="invoices" className="space-y-4">
            {invoices.length === 0 ? (
              <Card className="border-brand-border bg-white">
                <CardContent className="p-10 text-center">
                  <Receipt size={40} className="text-brand-text-secondary mx-auto mb-3" />
                  <p className="text-brand-text-primary font-semibold">No invoices yet</p>
                  <p className="text-sm text-brand-text-secondary mt-1">Invoices are automatically generated in INR (with GST) once your enrollment is approved by admin.</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-brand-border bg-white" data-testid="invoices-card">
                <CardHeader className="pb-2 border-b border-brand-border">
                  <CardTitle className="text-sm flex items-center gap-2"><Receipt size={16} /> Tax Invoices (GST · INR)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-brand-border bg-brand-bg/50">
                          {['Invoice #', 'Date', 'Course', 'Taxable', 'GST', 'Total (INR)', 'Payment', 'Action'].map(h => (
                            <th key={h} className="text-left py-2 px-4 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map(inv => (
                          <tr key={inv.id} className="border-b border-brand-border/50" data-testid={`invoice-row-${inv.id}`}>
                            <td className="py-2 px-4 font-mono text-xs text-brand-text-primary">{inv.invoice_number}</td>
                            <td className="py-2 px-4 text-xs text-brand-text-secondary">{inv.invoice_date}</td>
                            <td className="py-2 px-4 text-xs text-brand-text-primary">{inv.course_title}</td>
                            <td className="py-2 px-4 font-mono text-xs">₹{Number(inv.taxable_value_inr || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="py-2 px-4 font-mono text-xs">₹{Number(inv.total_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="py-2 px-4 font-mono text-xs font-bold text-brand-text-primary">₹{Number(inv.total_inr || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="py-2 px-4 text-xs uppercase text-brand-text-secondary">{inv.payment_mode || '-'}</td>
                            <td className="py-2 px-4">
                              <Button
                                size="sm"
                                onClick={() => downloadInvoice(inv)}
                                disabled={downloadingInvoice === inv.id}
                                className="bg-brand-primary text-white text-xs h-7"
                                data-testid={`download-invoice-${inv.id}`}
                              >
                                {downloadingInvoice === inv.id ? <Loader2 size={12} className="animate-spin" /> : <><Download size={12} className="mr-1" /> PDF</>}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
              {/* Verified card + ID download */}
              <Card className="border-brand-border bg-gradient-to-br from-brand-primary/5 to-brand-primary/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-brand-success/15 flex items-center justify-center">
                      <ShieldCheck size={22} className="text-brand-success" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Verification</p>
                      <p className="font-heading text-lg font-bold text-brand-success">KYC Verified ✓</p>
                    </div>
                  </div>
                  <p className="text-xs text-brand-text-secondary mb-4">Your identity is verified. You can now download your official Quantora Academy ID card.</p>
                  <div className="text-xs space-y-1 mb-4">
                    <div className="flex justify-between"><span className="text-brand-text-secondary">ID Type</span><span className="font-mono text-brand-text-primary">{(me?.kyc_id_type || '-').toUpperCase()}</span></div>
                    <div className="flex justify-between"><span className="text-brand-text-secondary">Verified On</span><span className="font-mono text-brand-text-primary">{(me?.kyc_verified_at || '').slice(0, 10) || '-'}</span></div>
                  </div>
                  <Button onClick={downloadIdCard} className="w-full bg-brand-primary text-white" data-testid="download-id-card-btn">
                    <IdCard size={14} className="mr-2" /> Download Student ID Card
                  </Button>
                </CardContent>
              </Card>

              {/* Profile details */}
              <Card className="border-brand-border bg-white">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User size={16} /> My Profile</CardTitle></CardHeader>
                <CardContent className="space-y-1 pt-2">
                  {[
                    ['Student ID', me?.student_id],
                    ['Full Name', me?.kyc_full_name || me?.name],
                    ['Email', me?.email],
                    ['Phone', me?.phone || '-'],
                    ['Date of Birth', me?.kyc_dob || '-'],
                    ['Gender', (me?.kyc_gender || '-').toString().toUpperCase()],
                    ['State', me?.kyc_state || '-'],
                    ['Status', (me?.status || '-').toUpperCase()],
                    ['Joined', me?.created_at?.slice(0, 10)],
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between py-2 border-b border-brand-border/50">
                      <span className="text-xs text-brand-text-secondary">{l}</span>
                      <span className="text-xs font-medium text-brand-text-primary">{v || '-'}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <EnrollDialog course={enrollDialog} onClose={() => setEnrollDialog(null)} form={enrollForm} setForm={setEnrollForm} onSubmit={handleEnroll} enrolling={enrolling} />
    </div>
  );
}

// ─── Sub-components ───

function TopBar({ name, onLogout }) {
  return (
    <header className="bg-white border-b border-brand-border px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-primary flex items-center justify-center">
          <GraduationCap size={18} className="text-white" />
        </div>
        <div>
          <h1 className="font-heading text-base font-bold text-brand-primary leading-none">Quantora Academy</h1>
          <p className="text-[10px] text-brand-text-secondary uppercase tracking-widest">Student Portal</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-brand-text-secondary hidden sm:block">{name}</span>
        <Button variant="outline" size="sm" onClick={onLogout} className="border-brand-border" data-testid="student-logout-btn">
          <LogOut size={14} className="mr-1" /> Logout
        </Button>
      </div>
    </header>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
      <Icon size={16} className="text-amber-300 mb-1" />
      <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{label}</p>
      <p className="font-heading text-xl font-bold">{value}</p>
    </div>
  );
}

function EnrollDialog({ course, onClose, form, setForm, onSubmit, enrolling }) {
  if (!course) return null;
  return (
    <Dialog open={!!course} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle>Enroll in {course.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-brand-bg rounded-lg p-3 border border-brand-border/50">
            <div className="flex justify-between text-sm">
              <span className="text-brand-text-secondary">Course Fee</span>
              <span className="font-heading font-bold text-brand-primary">${course.fee_amount}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Payment Method</Label>
            <Select value={form.payment_mode} onValueChange={v => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger className="bg-white border-brand-border h-9 text-sm" data-testid="enroll-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="upi">UPI Transfer</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="crypto_usdt_trc20">Crypto (USDT TRC20)</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Payment Reference (UTR / TXID)</Label>
            <Input value={form.payment_reference} onChange={e => setForm({ ...form, payment_reference: e.target.value })} placeholder="Optional but recommended" className="bg-white border-brand-border h-9 text-sm" data-testid="enroll-payment-ref" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional" rows={2} className="bg-white border-brand-border text-sm" />
          </div>
          <p className="text-xs text-brand-text-secondary">Your enrollment will be reviewed and activated by admin after payment verification.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} disabled={enrolling} className="bg-brand-primary text-white" data-testid="enroll-submit-btn">
            {enrolling ? <Loader2 className="animate-spin" size={16} /> : 'Submit Enrollment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
